import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useFilters } from '../99-contexts/FilterContext';

const ServiceSearchAndFilters: React.FC = () => {
  const { filters, updateFilter, resetFilters, hasActiveFilters } = useFilters();
  const [showFilters, setShowFilters] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState(filters.searchTerm);

  // Debounce pour la recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      updateFilter('searchTerm', localSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearchTerm, updateFilter]);

  // Synchroniser avec le contexte
  useEffect(() => {
    setLocalSearchTerm(filters.searchTerm);
  }, [filters.searchTerm]);

  const handleFilterChange = (field: string, value: string) => {
    updateFilter(field as keyof typeof filters, value);
  };

  const handleReset = () => {
    resetFilters();
    setLocalSearchTerm('');
  };

  return (
    <section className="p-6 bg-white shadow rounded-xl">
      {/* Main Search Bar - Google Style */}
      <div className="flex items-center space-x-4">
        {/* Search Input */}
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un prix, un matériau, un fournisseur..."
            value={localSearchTerm}
            onChange={(e) => setLocalSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          />
          {localSearchTerm && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="w-2 h-2 bg-violet-500 rounded-full animate-pulse"></div>
            </div>
          )}
        </div>

        {/* Filter Toggle - Discreet Design */}
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            <span className="text-sm font-medium">Filtres</span>
            <ChevronDownIcon 
              className={`w-4 h-4 transition-transform duration-200 ${
                showFilters ? 'rotate-180' : ''
              }`} 
            />
            {hasActiveFilters && (
              <div className="w-1.5 h-1.5 bg-violet-500 rounded-full ml-1"></div>
            )}
          </button>

          {/* Reset Button */}
          {hasActiveFilters && (
            <button
              onClick={handleReset}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Réinitialiser
            </button>
          )}
        </div>
      </div>

      {/* Filters Panel - Collapsible */}
      {showFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <select 
              value={filters.typologie}
              onChange={(e) => handleFilterChange('typologie', e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="">Typologie</option>
              <option value="residentiel">Résidentiel</option>
              <option value="commercial">Commercial</option>
              <option value="industriel">Industriel</option>
            </select>
            
            <select 
              value={filters.annee}
              onChange={(e) => handleFilterChange('annee', e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="">Année</option>
              <option value="2024">2024</option>
              <option value="2023">2023</option>
              <option value="2022">2022</option>
            </select>
            
            <select 
              value={filters.localisation}
              onChange={(e) => handleFilterChange('localisation', e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="">Localisation</option>
              <option value="paris">Paris</option>
              <option value="lyon">Lyon</option>
              <option value="marseille">Marseille</option>
            </select>
            
            <input 
              placeholder="Projet" 
              value={filters.projet}
              onChange={(e) => handleFilterChange('projet', e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            
            <input 
              placeholder="Fournisseur" 
              value={filters.fournisseur}
              onChange={(e) => handleFilterChange('fournisseur', e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            
            <select 
              value={filters.categorie}
              onChange={(e) => handleFilterChange('categorie', e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="">Catégorie</option>
              <option value="maçonnerie">Maçonnerie</option>
              <option value="electricite">Électricité</option>
              <option value="plomberie">Plomberie</option>
            </select>
          </div>

          {/* Price Range - Prix unitaire */}
          <div className="mt-3">
            <div className="mb-2">
              <label className="text-sm font-medium text-gray-700">Prix unitaire (€/unité)</label>
            </div>
            <div className="flex space-x-3">
              <input 
                placeholder="Prix min €/unité" 
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                value={filters.prixMin}
                onChange={(e) => handleFilterChange('prixMin', e.target.value)}
              />
              <span className="flex items-center text-gray-500">à</span>
              <input 
                placeholder="Prix max €/unité" 
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                value={filters.prixMax}
                onChange={(e) => handleFilterChange('prixMax', e.target.value)}
              />
            </div>
          </div>

          {/* Status Indicator */}
          {hasActiveFilters && (
            <div className="mt-4 p-3 bg-violet-50 border border-violet-200 rounded-lg">
              <div className="flex items-center space-x-2 text-sm text-violet-700">
                <div className="w-2 h-2 bg-violet-500 rounded-full animate-pulse"></div>
                <span>Filtres actifs - résultats mis à jour automatiquement</span>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default ServiceSearchAndFilters;
